import { useContext, useEffect, useRef, useState } from "react";
import { AuthContext, BrandContext } from "../contexts";
import { isTouchMobile } from "../utils/device";
import { getShareLink } from "../utils/shareLink";
import { Btn } from "./ui";

const ShareIcon = ({ copied }) => copied ? (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
) : (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7"/><path d="m16 6-4-4-4 4"/><path d="M12 2v13"/></svg>
);

const visuallyHidden = { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap", border: 0 };

export default function ShareButton({ product, variant = "button" }) {
  const { user } = useContext(AuthContext);
  const brand = useContext(BrandContext);
  const [copied, setCopied] = useState(false);
  const [readyUrl, setReadyUrl] = useState(null);
  const timer = useRef();
  useEffect(() => () => clearTimeout(timer.current), []);
  // Resolve the link (including the affiliate lookup) BEFORE the tap. Safari only opens the
  // share sheet / writes the clipboard straight from a user gesture; an await inside the click
  // handler can spend that gesture and the share silently fails on iPhone.
  useEffect(() => {
    let live = true;
    setReadyUrl(null);
    getShareLink(product.id, user).then(url => { if (live) setReadyUrl(url); }).catch(() => {});
    return () => { live = false; };
  }, [product.id, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShare = async event => {
    event.stopPropagation();
    const url = readyUrl || await getShareLink(product.id, user);
    const brandName = [brand.name, brand.nameAccent].filter(Boolean).join(" ");
    if (isTouchMobile() && navigator.share) {
      try {
        await navigator.share({ title: `${product.name} · ${brandName}`, url });
      } catch (error) {
        if (error?.name !== "AbortError") console.error("Share failed:", error);
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Copy share link failed:", error);
    }
  };

  const content = <><ShareIcon copied={copied} />{(variant === "button" || copied) && <span>{copied ? "Link copied" : "Share"}</span>}<span style={visuallyHidden}>{variant === "button" ? ` ${product.name}` : `Share ${product.name}`}</span></>;
  if (variant === "button") {
    return <Btn variant="ghost" small onClick={handleShare} style={copied ? { color: "var(--green)", borderColor: "var(--green)", flexShrink: 0 } : { flexShrink: 0 }}>{content}</Btn>;
  }
  return (
    <button type="button" aria-label={`Share ${product.name}`} onClick={handleShare} style={{
      position: "absolute", top: 10, right: 10, zIndex: 1, display: "inline-flex", alignItems: "center", gap: 6,
      height: 32, minWidth: 32, justifyContent: "center", padding: "0 8px", cursor: "pointer",
      background: "rgba(8,8,16,.72)", color: copied ? "var(--green)" : "var(--text)",
      border: `1px solid ${copied ? "var(--green)" : "var(--border)"}`, borderRadius: 16,
      fontFamily: "var(--font-mono)", fontSize: 11,
    }}>{content}</button>
  );
}
